import React, { useState, useCallback, useRef, useEffect } from "react";
import { Select, Button, Modal, Tag, Switch } from "antd";
import { ExclamationCircleOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import Axios from "axios";
import Treeselect from "../../../../../components/common/Treeselect";

const { confirm } = Modal;

const FilterCombinations = ({
    combinations,
    setCombinations,
    categoryTreeOptions,
    categoriesOptions,
    brandsOptions,
    suppliersOptions,
    onSearchBrands,
    onSearchSuppliers,
}) => {
    const [editingIdx, setEditingIdx] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState({
        categories: [],
        brands: [],
        suppliers: [],
        filter_type: "include",
    });

    const resetDraft = () => {
        setDraft({ categories: [], brands: [], suppliers: [], filter_type: "include" });
    };

    const handleSaveNew = () => {
        if (
            draft.categories.length === 0 &&
            draft.brands.length === 0 &&
            draft.suppliers.length === 0
        ) {
            return;
        }
        setCombinations((prev) => [...prev, { ...draft }]);
        resetDraft();
        setIsAdding(false);
    };

    const handleSaveEdit = () => {
        if (editingIdx === null) return;
        setCombinations((prev) => {
            const updated = [...prev];
            updated[editingIdx] = { ...draft, id: updated[editingIdx].id };
            return updated;
        });
        resetDraft();
        setEditingIdx(null);
    };

    const handleStartEdit = (idx) => {
        const combo = combinations[idx];
        setDraft({
            categories: combo.categories || [],
            brands: combo.brands || [],
            suppliers: combo.suppliers || [],
            filter_type: combo.filter_type || "include",
        });
        setEditingIdx(idx);
        setIsAdding(false);
    };

    const handleStartEditVisible = (originalIdx) => {
        handleStartEdit(originalIdx);
    };

    const handleCancelEdit = () => {
        resetDraft();
        setEditingIdx(null);
        setIsAdding(false);
    };

    const handleDelete = (idx) => {
        confirm({
            title: "Are you sure?",
            icon: <ExclamationCircleOutlined className="text-primary" />,
            content: "You will not be able to undo this action",
            onOk() {
                setCombinations((prev) => {
                    const updated = [...prev];
                    if (updated[idx].id) {
                        updated[idx] = { ...updated[idx], _destroy: true };
                    } else {
                        updated.splice(idx, 1);
                    }
                    return updated;
                });
                if (editingIdx === idx) {
                    resetDraft();
                    setEditingIdx(null);
                }
            },
        });
    };

    const handleStartAdd = () => {
        resetDraft();
        setEditingIdx(null);
        setIsAdding(true);
    };

    const visibleCombinations = combinations
        .map((combo, idx) => ({ ...combo, _originalIdx: idx }))
        .filter((combo) => !combo._destroy);

    const isEditing = editingIdx !== null;
    const isFormOpen = isAdding || isEditing;

    const TAG_LIMIT = 5;

    const colorMap = {
        blue: { background: "#0050b3", text: "#fff" },
        green: { background: "#237804", text: "#fff" },
        orange: { background: "#d46b08", text: "#fff" },
    };

    const renderTag = (item, color) => {
        const c = colorMap[color] || { background: color, text: "#fff" };
        return (
            <Tag
                key={item.value}
                style={{
                    marginBottom: 2,
                    fontSize: "10px",
                    backgroundColor: c.background,
                    color: c.text,
                    border: "none",
                }}
            >
                {item.label}
            </Tag>
        );
    };

    return (
        <div
            className="p-2"
            style={{
                backgroundColor: "#F5F5F5",
                border: "1px solid #EFEFEF",
                borderRadius: 1,
            }}
        >
            {visibleCombinations.map((combo, visibleIdx) => (
                <div
                    key={combo.id || `new-${combo._originalIdx}`}
                    className="p-2 mb-2"
                    style={{
                        backgroundColor: editingIdx === combo._originalIdx ? "#e6f7ff" : "#fff",
                        border: `1px solid ${editingIdx === combo._originalIdx ? "#91d5ff" : "#EFEFEF"}`,
                        borderRadius: 1,
                    }}
                >
                    <div className="d-flex justify-content-between align-items-start">
                        <div style={{ flex: 1 }}>
                            <div className="d-flex align-items-center mb-1" style={{ gap: 6 }}>
                                <span className="text-xs font-weight-bold">
                                    Combination #{visibleIdx + 1}
                                </span>
                                <Tag
                                    style={{
                                        fontSize: "10px",
                                        margin: 0,
                                        backgroundColor: combo.filter_type === "exclude" ? "#cf1322" : "#389e0d",
                                        color: "#fff",
                                        border: "none",
                                    }}
                                >
                                    {combo.filter_type === "exclude" ? "Exclude" : "Include"}
                                </Tag>
                            </div>
                            {combo.categories?.length > 0 && (
                                <CollapsibleTags
                                    label="Categories"
                                    items={combo.categories}
                                    color="blue"
                                    renderTag={renderTag}
                                    limit={TAG_LIMIT}
                                />
                            )}
                            {combo.brands?.length > 0 && (
                                <CollapsibleTags
                                    label="Brands"
                                    items={combo.brands}
                                    color="green"
                                    renderTag={renderTag}
                                    limit={TAG_LIMIT}
                                />
                            )}
                            {combo.suppliers?.length > 0 && (
                                <CollapsibleTags
                                    label="Suppliers"
                                    items={combo.suppliers}
                                    color="orange"
                                    renderTag={renderTag}
                                    limit={TAG_LIMIT}
                                />
                            )}
                        </div>
                        <div className="d-flex" style={{ gap: 4 }}>
                            <Button
                                size="small"
                                type="link"
                                className="p-0"
                                style={{ color: "green" }}
                                disabled={isFormOpen && editingIdx !== combo._originalIdx}
                                onClick={() => handleStartEditVisible(combo._originalIdx)}
                                icon={<EditOutlined />}
                            />
                            <Button
                                size="small"
                                type="link"
                                className="p-0"
                                danger
                                onClick={() => handleDelete(combo._originalIdx)}
                                icon={<DeleteOutlined />}
                            />
                        </div>
                    </div>
                </div>
            ))}

            {isFormOpen && (
                <div
                    className="p-2 mb-2"
                    style={{
                        backgroundColor: "#fff",
                        border: "1px solid #EFEFEF",
                        borderRadius: 1,
                    }}
                >
                    <div className="text-xs font-weight-bold mb-2">
                        {isEditing
                            ? `Editing combination #${editingIdx + 1}`
                            : "New combination"}
                    </div>

                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                        <span className="text-xs">Include</span>
                        <Switch
                            size="small"
                            checked={draft.filter_type === "exclude"}
                            onChange={(checked) =>
                                setDraft((prev) => ({ ...prev, filter_type: checked ? "exclude" : "include" }))
                            }
                        />
                        <span className="text-xs">Exclude</span>
                    </div>

                    <div className="text-xs">Categories</div>
                    <Treeselect
                        options={categoryTreeOptions}
                        value={draft.categories.map((c) => c.value)}
                        onChange={(values) => {
                            const formatted = values.map((val) => {
                                const id = typeof val === "string" && val.startsWith("00_")
                                    ? parseInt(val.substring(3)) : val;
                                const cat = categoriesOptions.find((c) => c.value === id);
                                return { label: cat?.label || `Category ${id}`, value: id };
                            });
                            setDraft((prev) => ({ ...prev, categories: formatted }));
                        }}
                        placeholder="Select categories..."
                        multiSelect={true}
                        showStrategy="SHOW_ALL"
                    />

                    <div className="text-xs">Brands</div>
                    <Select
                        mode="multiple"
                        options={brandsOptions}
                        value={draft.brands}
                        dropdownMatchSelectWidth={false}
                        labelInValue
                        showSearch
                        filterOption={false}
                        onChange={(value) =>
                            setDraft((prev) => ({ ...prev, brands: value }))
                        }
                        onSearch={onSearchBrands}
                        placeholder="Search brands..."
                        style={{ width: "100%", marginBottom: 6 }}
                        size="small"
                    />

                    <div className="text-xs">Suppliers</div>
                    <Select
                        mode="multiple"
                        options={suppliersOptions}
                        value={draft.suppliers}
                        dropdownMatchSelectWidth={false}
                        labelInValue
                        showSearch
                        filterOption={false}
                        onChange={(value) =>
                            setDraft((prev) => ({ ...prev, suppliers: value }))
                        }
                        onSearch={onSearchSuppliers}
                        placeholder="Search suppliers..."
                        style={{ width: "100%", marginBottom: 8 }}
                        size="small"
                    />

                    <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                        <Button size="small" onClick={handleCancelEdit}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            type="primary"
                            onClick={isEditing ? handleSaveEdit : handleSaveNew}
                            disabled={
                                draft.categories.length === 0 &&
                                draft.brands.length === 0 &&
                                draft.suppliers.length === 0
                            }
                        >
                            {isEditing ? "Save" : "Add"}
                        </Button>
                    </div>
                </div>
            )}

            {!isFormOpen && (
                <Button
                    size="small"
                    type="link"
                    className="text-primary p-0"
                    onClick={handleStartAdd}
                    icon={<PlusOutlined />}
                >
                    Add combination
                </Button>
            )}
        </div>
    );
};

export default FilterCombinations;

const CollapsibleTags = ({ label, items, color, renderTag, limit }) => {
    const [expanded, setExpanded] = useState(false);
    const contentRef = useRef(null);
    const [contentHeight, setContentHeight] = useState("auto");

    const hasMore = items.length > limit;
    const visibleItems = hasMore && !expanded ? items.slice(0, limit) : items;
    const hiddenCount = items.length - limit;

    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight + "px");
        }
    }, [items, expanded]);

    return (
        <div className="mb-1">
            <span className="text-xs" style={{ color: "#999" }}>{label}: </span>
            <div
                ref={contentRef}
                style={{
                    display: "inline",
                    maxHeight: expanded ? contentHeight : undefined,
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                {visibleItems.map((item) => renderTag(item, color))}
            </div>
            {hasMore && (
                <span
                    onClick={() => setExpanded((prev) => !prev)}
                    style={{
                        fontSize: "10px",
                        color: "#1890ff",
                        cursor: "pointer",
                        marginLeft: 4,
                        userSelect: "none",
                    }}
                >
                    {expanded ? "Show less" : `+${hiddenCount} more`}
                </span>
            )}
        </div>
    );
};
